import { useQuery } from "@apollo/client"
import CustomSpinner from "components/Atoms/CustomSpinner"
import Codenames from "constants/codenames"
import { COLOURS, ESTIMATED_DELIVERY_DAYS } from "data/static.items"
import dayjs from "dayjs"
import { useDistributionCenterDetails } from "graphql/distribution-center/distribution-center.hooks"
import { STATES } from "graphql/selector.queries.gql"
import { omit, pick } from "lodash-es"
import React, { useEffect } from "react"
import { Route, Routes, useParams } from "react-router-dom"
import classNames from "utils/classNames"
import { gisToJson, gisToJson2 } from "utils/map.utils"
import styles from "./dc.module.scss"
import useDCStore from "./dc.store"
import DCFormSections, { dcFormTabs } from "./DCFormSections"
import { PAYMENT_METHOD_TYPES } from "./payment-methods/PaymentMethod"
type DCRegistrationProps = {
  className?: string
}

const mapTimeSlot = (timeSlot: any) => {
  const timeSlots = pick(timeSlot, ["fromTime", "toTime"])

  const threshHold = timeSlot?.thresholdFrom?.split(":")?.map(s => parseInt(s))
  return {
    id: timeSlot?.id,
    slots: Object.values(timeSlots).map(ts => {
      const time = ts.split(":").map(s => parseInt(s))
      if (ts === "23:59:59") {
        return 24
      }
      return time[0] + time[1] / 60 + time[2] / 3600
    }),
    maximumOrders: timeSlot?.maximumOrders,
    thresholdFrom: threshHold?.length
      ? dayjs()
          .set("hour", threshHold[0])
          .set("minutes", threshHold[1])
          .set("seconds", threshHold[2])
      : undefined,
    estimatedDelivery: ESTIMATED_DELIVERY_DAYS.find(
      ed => ed.id === timeSlot?.estimatedDelivery
    ),
  }
}

const formChildren = dcFormTabs.reduce((prev, item) => {
  return prev.concat(...item.children)
}, [])
const DCRegistration = ({ className }: DCRegistrationProps) => {
  const { id } = useParams<{ id: string }>()
  const resetState = useDCStore(store => store.reset)
  const { data } = useQuery(STATES)
  const initializeData = useDCStore(store => store.initializeState)
  const [getDetails, { loading }] = useDistributionCenterDetails(
    ({ distributionCentreLocationDetail: dcl }) => {
      if (!parseInt(id) && !data?.allStates) return
      const dc = dcl?.properties
      const deliveryConditions = dc.deliveryConditions
      const reversedGeometry = {
        ...dcl.geometry,
        coordinates: dcl.geometry?.coordinates.map(polygon =>
          polygon.map(ring => ring.map(([lat, lng]) => [lng, lat]))
        ),
      }

      const state = {
        basicDetails: {
          ...pick(dc, [
            "archive",
            "codename",
            "storeCode",
            "plantCode",
            "companyCode",
            "customerCode",
            "salesOfcCode",
            "partnerCode",
            "primaryPoc",
            "assignedCpc",
            "name",
            "groupVisibility",
            "pointOfContacts",
            "allowedDepartments",
          ]),
          isManufacturingPlant: deliveryConditions.isManufacturingPlant
            ? "yes"
            : "no",
          address: dc.address as any,
          colorCode: COLOURS.find(c => c.id === dc.colorCode),
          id: dcl?.id,
          geometry: reversedGeometry,
          photos: dc.photos,
        },
        surveyAreas: dc.surveyareaSet.map(area => ({
          id: area.id,
          areaCatered: gisToJson2(area.geometry.coordinates?.[0]),
          ...pick(area.properties, [
            "name",
            "codename",
            "users",
            "primaryUser",
            "isActive",
          ]),
        })),
        paymentMethods: dc.bankDetails?.length
          ? dc.bankDetails.map(method => ({
              ...method,
              paymentMethod:
                method?.bank.codename === Codenames.UPI
                  ? PAYMENT_METHOD_TYPES.find(m => m.id === Codenames.UPI)
                  : PAYMENT_METHOD_TYPES.find(m => m.id === "BANK"),
            }))
          : [{} as any],
        licenseDetails: dc.certifications?.length
          ? dc.certifications.map(cert => ({
              ...cert,
              hasValidity: cert.validTill ? "yes" : "no",
            }))
          : [{ hasValidity: "no" } as any],
        operationalSlots: {
          deliverySlots: dc.deliverySlots.map(mapTimeSlot),
          pickupSlots: dc.pickupSlots.map(mapTimeSlot),
          maintenanceSlots: dc.maintenanceSlots.map(mapTimeSlot),
          alternateSalesSlots: dc.alternateSalesSlots.map(mapTimeSlot),
        },
        deliveryCharges: {
          ...deliveryConditions,
          deliveryCosts: deliveryConditions?.deliveryCosts?.length
            ? deliveryConditions?.deliveryCosts
            : [{} as any],
          estimatedDelta: ESTIMATED_DELIVERY_DAYS.find(
            item => item.id === deliveryConditions?.estimatedDelta
          ),
        },
        billingDetails: {
          ...omit(deliveryConditions.gstDetails, ["stateName", "stateCode"]),
          state: data?.allStates?.find(
            state =>
              parseInt(state.codename) ===
              parseInt(deliveryConditions.gstDetails?.stateCode)
          ),
          ...deliveryConditions.extraSapCodes,
          applicable: deliveryConditions.gstDetails?.gstin ? "yes" : "no",
        },
        stockPreferences: {
          currentStockPercentage: dc?.currentStockPercentage,
        },
      }
      initializeData({
        state: state as any,
        lastUpdated: state as any,
        changed: {},
        disabled: false,
        errors: {},
      })
    }
  )
  useEffect(() => {
    if (parseInt(id)) {
      getDetails({ variables: { id } })
    }
    return function cleanup() {
      resetState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
  if (loading) return <CustomSpinner />

  return (
    <div
      className={classNames(
        "vt-distribution-center-registration",
        styles.wrapper,
        className
      )}
    >
      <DCFormSections />

      <div className={classNames(styles.card, styles.content)}>
        <Routes>
          {formChildren.map(child => {
            const Component = child.component
            return (
              <Route key={child.id} path={child.id} element={<Component />} />
            )
          })}
        </Routes>
      </div>
    </div>
  )
}

export default DCRegistration
